import React, {ChangeEvent} from "react";
import {useNavigate, useParams} from "react-router-dom";

import DropDownMenu, {IChoice, findOptionFromList} from "@components/inputs/DropDownMenu";
import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import {NavBar} from "@components/navigation/NavBar/NavBar";
import LargeTextbox from "@components/inputs/text/LargeTextbox";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";

import {
    ExchangeReasonAction,
    ExchangeReasons,
    ExchangeReasonState,
    ExchangeTypes,
    isReplacementVehicleExchange
} from "./exchangeReasonReducer";
import InfoBox from "../../components/cards/messageBox/InfoBox";
import WarningBox from "../../components/cards/messageBox/WarningBox";
import Checkbox from "@components/inputs/Checkbox";
import {SelectedSubscriptionState} from "@views/VehicleExchange/selectedSubscriptionReducer";
import {PlanOptions} from "@utils/planOptions";

// Map the possible values to display labels. The key is what is dispatched and sent to the BE
export const ExchangeTypeLabels: Record<ExchangeTypes, string> = {
    [ExchangeTypes.Permanent]: "Permanent Vehicle Exchange",
    [ExchangeTypes.Temporary]: "Temporary Vehicle Exchange",
    [ExchangeTypes.PriceChange]: "Permanent Vehicle Exchange (Price Change)",
    [ExchangeTypes.DAMS_Cross_Hire]: "DAMS Cross Hire",
};

export const ExchangeReasonLabels: Record<ExchangeReasons, string> = {
    [ExchangeReasons.AccidentReplacement]: "Accident Replacement",
    [ExchangeReasons.DefleetingVehicle]: "Defleeting Vehicle",
    [ExchangeReasons.ReturningToContractedVehicle]: "Returning to Contracted Vehicle",
    [ExchangeReasons.TotalLossReplacement]: "Total Loss Replacement",
    [ExchangeReasons.WarrantyReplacement]: "Warranty Replacement",
    [ExchangeReasons.SoldAsPreloved]: "Sold as Preloved",
    [ExchangeReasons.OutrightPurchase]: "Outright Purchase",
    [ExchangeReasons.PCOExpired]: "PCO Expired",
    [ExchangeReasons.InterstateTransfer]: "Interstate Transfer",
    [ExchangeReasons.VehicleUpgrade]: "Vehicle upgrade (or downgrade)"
};

// DAMS Cross Hire is offered to these plans only. An allowlist rather than a denylist, so a plan
// added to Zoho later does not silently become DAMS eligible.
// Note Flexi appears here but is excluded from Temporary below - that is deliberate, not an
// oversight. A DAMS customer comes back to the specific vehicle being repaired, so a Flexi
// customer does have something to return to; an ordinary temporary exchange for a Flexi customer
// has no contracted vehicle to return to at all.
const DAMS_ELIGIBLE_PLANS: string[] = [
    PlanOptions.UKFlexiOwn,
    PlanOptions.UKRentToOwnUsed,   // Pre-Loved Flexi-Own
    PlanOptions.UKFlexi,           // Splend Rent
];

// A price change changes the weekly price of a Flexi subscription, so only the Flexi plans may
// use it. An allowlist rather than a denylist, so a plan added to Zoho later does not silently
// become price changeable.
const PRICE_CHANGE_ELIGIBLE_PLANS: string[] = [
    PlanOptions.SplendFlexi,
    PlanOptions.UKFlexi,
];

// Flexi and RAV customers are not on a contracted vehicle, so they have nothing to come back to
const PLANS_WITHOUT_CONTRACTED_VEHICLE: string[] = [
    PlanOptions.SplendFlexi,
    PlanOptions.UKFlexi,
    PlanOptions.RAVCreditHire,
    PlanOptions.RAVFirstPartyHire,
    PlanOptions.RAVFirstPartyHireEV,
    PlanOptions.RAVCreditHireEV,
    PlanOptions.RAVSharecover,
    PlanOptions.RAVIAG,
    PlanOptions.RAVShortTermRental,
    PlanOptions.PHAVCreditHire,
    PlanOptions.PHAVShortTermRental,
    PlanOptions.PremiumPHAVCreditHire,
];

type Props = {
    navigateTarget: string;
    exchangeReasonState: ExchangeReasonState;
    exchangeReasonDispatch: React.Dispatch<ExchangeReasonAction>;
    selectedSubscriptionState: SelectedSubscriptionState;
};

export default function ExchangeReason({
                                           navigateTarget,
                                           exchangeReasonState,
                                           exchangeReasonDispatch,
                                           selectedSubscriptionState,
                                       }: Props) {
    const {country} = useParams();
    const navigate = useNavigate();

    const componentName = "Exchange reason";

    const isPriceChange = exchangeReasonState.exchangeType === ExchangeTypes.PriceChange;

    const shouldExchangeTypeOptionEnable = (possibleExchangeTypeOption: ExchangeTypes, selectedPlan: string | null): boolean => {
        // Uses the selected subscription to determine if the given exchange option is a valid choice (enabled)
        // Designed to allow by default, disable for specific required plans (Flexi + RAV)
        if (possibleExchangeTypeOption === ExchangeTypes.DAMS_Cross_Hire) {
            return DAMS_ELIGIBLE_PLANS.includes(selectedPlan ?? "");
        }
        if (possibleExchangeTypeOption === ExchangeTypes.Temporary) {
            return !PLANS_WITHOUT_CONTRACTED_VEHICLE.includes(selectedPlan ?? "");
        }
        if (possibleExchangeTypeOption === ExchangeTypes.PriceChange) {
            return PRICE_CHANGE_ELIGIBLE_PLANS.includes(selectedPlan ?? "");
        }
        return true;
    };

    const exchangeTypeOptions: IChoice[] = Object.values(ExchangeTypes).map((value) => ({
        value,
        label: ExchangeTypeLabels[value],
        enabled: shouldExchangeTypeOptionEnable(value, selectedSubscriptionState.selectedPlan),
    })).filter(opt => {
        // GB specific exchange type
        if (opt.value === ExchangeTypes.DAMS_Cross_Hire) {
            return country === "GB";
        }
        return true;
    });


    const exchangeReasonsOptions: IChoice[] = Object.values(ExchangeReasons).map((value) => ({
        value,
        label: ExchangeReasonLabels[value],
        enabled: true,
    })).filter(opt => {
        // AU specific exchange reason
        if (opt.value === ExchangeReasons.InterstateTransfer) {
            return country === "AU";
        }
        // Only a price change can be an upgrade or downgrade
        if (opt.value === ExchangeReasons.VehicleUpgrade) {
            return exchangeReasonState.exchangeType === ExchangeTypes.PriceChange;
        }
        return true;
    });

    const selectedExchangeType: IChoice =
        findOptionFromList(exchangeReasonState.exchangeType, exchangeTypeOptions);

    const selectedExchangeReason: IChoice =
        findOptionFromList(exchangeReasonState.exchangeReason, exchangeReasonsOptions);

    // Temporary and DAMS Cross Hire both put the customer into a vehicle they later hand back
    const reasonQuestion = isReplacementVehicleExchange(exchangeReasonState.exchangeType)
        ? "Why is this customer being temporarily exchanged out of their original vehicle?"
        : "Why is this customer being permanently exchanged out of their original vehicle?";

    const comments = exchangeReasonState.comments || "";

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        exchangeReasonDispatch({type: "USER_TYPES_COMMENTS", payload: value});
    };

    const onSelectExchangeType = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value as ExchangeTypes;
        exchangeReasonDispatch({type: "USER_SELECTS_EXCHANGE_TYPE", payload: value});
    };

    const onSelectExchangeReason = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value as ExchangeReasons;
        exchangeReasonDispatch({type: "USER_SELECTS_EXCHANGE_REASON", payload: value});
    };

    const onAcknowledgePriceChange = (checked: boolean) => {
        exchangeReasonDispatch({type: "USER_ACKNOWLEDGES_PRICE_CHANGE", payload: checked});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateTarget);
    };

    return (
        <>
            <NavBar pageTitle="Exchange reason" backAction={undefined}/>
            <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}></div>
            <InfoBox
                message={
                    <>
                        A <b>temporary exchange</b> means the current vehicle will need to be returned to the customer
                        in the future.
                        <br/><br/>
                        A <b>permanent exchange</b> means the current vehicle will not be returned to the customer in
                        the future.
                        <br/><br/>
                        A <b>price change</b> means the current vehicle will not be returned to the customer.
                        They will also sign an addendum agreeing to a new price for their weekly Flexi subscription.
                        {country === "GB" && (
                            <>
                                <br/><br/>
                                A <b>DAMS Cross Hire</b> is a temporary exchange used when the customer's
                                contracted vehicle goes off road for DAMS repair - the vehicle statuses are set
                                for you. To put the customer back into their contracted vehicle afterwards, use a
                                permanent exchange.
                            </>
                        )}
                    </>
                }
            />
            <form style={{margin: "2.5vh 2.5vw"}} onSubmit={handleSubmit}>
                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="Please select an exchange type:"/>
                    <DropDownMenu
                        menuName="exchangeType"
                        defaultVal={selectedExchangeType}
                        required
                        choices={exchangeTypeOptions}
                        onSelect={onSelectExchangeType}
                        testId="type-dropdown"
                    />
                </div>

                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text={reasonQuestion}/>
                    <DropDownMenu
                        menuName="exchangeReason"
                        defaultVal={selectedExchangeReason}
                        required
                        choices={exchangeReasonsOptions}
                        onSelect={onSelectExchangeReason}
                        key={exchangeReasonState.exchangeType}
                        testId="reason-dropdown"
                    />
                </div>

                {isPriceChange && (
                    <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                        <WarningBox
                            message={
                                <>
                                    A <b>price change</b> is for changing the weekly price of a Flexi
                                    subscription. The customer will sign an addendum commiting them to the new weekly
                                    price.
                                </>
                            }
                            testId="price-change-warning"
                        />
                        <div style={{margin: "1.5vh 0vw"}}>
                            <Checkbox
                                id="priceChangeAcknowledged"
                                name="priceChangeAcknowledged"
                                label=" I understand this will change the customer's weekly Flexi price"
                                required
                                checked={exchangeReasonState.priceChangeAcknowledged}
                                onChange={onAcknowledgePriceChange}
                                testId="price-change-acknowledge-checkbox"
                            />
                        </div>
                    </div>
                )}

                <div style={{display: "flex", flexDirection: "column", margin: "2.5vh 0vw"}}>
                    <Label text="Additional Comments:"/>
                    <LargeTextbox value={comments} onChange={handleCommentChange}/>
                </div>

                <SubmitBtn
                    text="Next"
                    breadcrumbObject={breadcrumbObject}
                    componentName={componentName}
                    disabled={isPriceChange && !exchangeReasonState.priceChangeAcknowledged}
                />
            </form>
        </>
    );
}
